import { quantidadeLinhasTelefone } from '@/lib/quantidadeLinhasTelefone';
import { supabase } from '@/integrations/supabase/client';
import { regraPorProduto, conflitoOperadora, to55 } from '@/lib/operadoraCoerencia';
import { produtoTemChip } from '@/lib/tipoChipAtivacao';

// Coerência produto × operadora (Douglas 07/08) — mesma régua do backend
// (pedido-validacao.service.ts): PN NOVO exige linha de OUTRA operadora;
// TT/RENOVAÇÃO/MP exigem CLARO. Fonte = cache telefones_operadora (60 dias);
// linha fora do cache NÃO bloqueia. Mudou aqui, muda lá.
type ItemComLinhas = {
  ddd?: string | null;
  linha?: string | null;
  /** No row do banco vem como Json — o loop faz o cast estreito. */
  linhas_telefone?: unknown;
};

async function conferirOperadoraItem(item: ItemComLinhas, produtoNome: string | null | undefined): Promise<string[]> {
  const regra = regraPorProduto(produtoNome);
  if (!regra) return [];

  const fones: Array<{ ddd: string; linha: string }> = [];
  if (item?.ddd && item?.linha) fones.push({ ddd: String(item.ddd), linha: String(item.linha) });
  const linhasTel = (Array.isArray(item?.linhas_telefone) ? item.linhas_telefone : []) as
    Array<{ ddd?: string | null; linha?: string | null }>;
  for (const l of linhasTel) {
    if (l?.ddd && l?.linha) fones.push({ ddd: String(l.ddd), linha: String(l.linha) });
  }
  const mapa = new Map<string, { ddd: string; linha: string }>();
  for (const f of fones) {
    const t = to55(`${f.ddd}${f.linha}`);
    if (t) mapa.set(t, f);
  }
  if (mapa.size === 0) return [];

  const cacheValido = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
  const { data } = await supabase
    .from('telefones_operadora' as never)
    .select('telefone, operadora, ultima_consulta')
    .in('telefone', [...mapa.keys()])
    .gte('ultima_consulta', cacheValido);

  const problemas: string[] = [];
  for (const r of ((data || []) as unknown as Array<{ telefone: string; operadora: string | null }>)) {
    const f = mapa.get(r.telefone);
    const msg = conflitoOperadora(regra, r.operadora);
    if (f && msg) problemas.push(`Linha (${f.ddd}) ${f.linha}: ${msg}`);
  }
  return problemas;
}

// Faixa da renovação obrigatória (Douglas 01/09) — mesma régua do backend
// (pedido-validacao.service.ts -> linhasSemFaixaRenovacao). A comissão da
// renovação é por LINHA: sem `faixa` apurada a linha cai na base legada, que
// existe só para o pedido vendido antes do montador de composição. Item de
// renovação não passa para pedido fechado sem faixa em todas as linhas.
// Mudou aqui, muda lá.
const ehRenovacao = (produtoNome: string | null | undefined) => {
  const n = String(produtoNome ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();
  return n === 'RENOVACAO';
};

function linhasSemFaixaRenovacao(item: ItemComLinhas & { quantidade?: unknown }, produtoNome: string | null | undefined): string[] {
  if (!ehRenovacao(produtoNome)) return [];
  const linhas = (Array.isArray(item?.linhas_telefone) ? item.linhas_telefone : []) as Array<Record<string, unknown>>;
  const qtd = Number(item?.quantidade || 0);
  const consideradas = qtd > 0 ? linhas.slice(0, qtd) : linhas;
  if (consideradas.length === 0) {
    return ['Composição da renovação (nenhuma linha para apurar a faixa)'];
  }
  const semFaixa = consideradas.filter((linha) => {
    if (!linha || typeof linha !== 'object') return true;
    const faixa = Number(linha.faixa ?? linha.faixa_apurada);
    return !Number.isFinite(faixa) || faixa < 1 || faixa > 7;
  });
  if (semFaixa.length === 0) return [];
  const numeros = semFaixa
    .map((linha) => `(${linha.ddd ?? '--'}) ${linha.linha ?? 'sem número'}`)
    .slice(0, 5)
    .join(', ');
  return [`Composição da renovação — faixa do delta faltando em ${semFaixa.length} linha(s): ${numeros}`];
}

export interface ItemIncompleto {
  id: string;
  numero: string;
  plano_nome: string;
  camposFaltantes: string[];
}

export interface ValidacaoPedido {
  valido: boolean;
  itensIncompletos: ItemIncompleto[];
  totalItens: number;
  itensCompletos: number;
}

/**
 * Valida um ITEM individual antes de fechar
 */
export async function validarItemParaFechar(itemId: string): Promise<{
  valido: boolean;
  camposFaltantes: string[];
}> {
  try {
    // Buscar item
    const { data: item, error: itemError } = await supabase
      .from('itens_pedido')
      .select('*')
      .eq('id', itemId)
      .single();

    if (itemError || !item) {
      console.error('Item não encontrado:', itemError);
      return { valido: false, camposFaltantes: ['Item não encontrado'] };
    }

    // Buscar produto separadamente
    const { data: prodData } = await supabase
      .from('produtos')
      .select('id, nome, grupo_obrigatoriedade_id')
      .eq('id', item.produto_id)
      .maybeSingle();

    // Régua universal: roda mesmo sem grupo de obrigatoriedades.
    const conflitosOperadora = [
      ...await conferirOperadoraItem(item, prodData?.nome),
      ...linhasSemFaixaRenovacao(item, prodData?.nome),
    ];

    if (!prodData?.grupo_obrigatoriedade_id) {
      return { valido: conflitosOperadora.length === 0, camposFaltantes: conflitosOperadora };
    }

    // Buscar configs sem join
    const { data: configsData } = await supabase
      .from('grupo_obrigatoriedades_config')
      .select('id, obrigatoriedade_id')
      .eq('grupo_id', prodData.grupo_obrigatoriedade_id)
      .eq('ativo', true)
      .eq('obrigatorio_em_pedido_fechado', true);

    if (!configsData || configsData.length === 0) {
      return { valido: conflitosOperadora.length === 0, camposFaltantes: conflitosOperadora };
    }

    // Buscar catálogo separadamente
    const obrigIds = configsData.map((c: any) => c.obrigatoriedade_id);
    const { data: catalogData } = await supabase
      .from('obrigatoriedades_catalogo')
      .select('id, codigo, nome')
      .in('id', obrigIds);

    const catalogos: any[] = catalogData || [];
    const camposFaltantes: string[] = [...conflitosOperadora];

    // ✅ VALIDAÇÃO UNIVERSAL TIPO_CHIP (independente de obrigatoriedade configurada)
    // ⚠️ EXCEÇÃO: Produtos TV não precisam de tipo_chip
    const produtoNomeValidacao = item.plano_nome || '';
    const isTV = produtoNomeValidacao.toUpperCase().includes('TV');
    const qtdTelefones = quantidadeLinhasTelefone(item.quantidade, item.linhas_telefone, (prodData?.nome || '').toUpperCase().includes('FIBRA'));

    // TT e renovação não têm chip: a linha já é do cliente e continua com o
    // mesmo número e o mesmo chip. O gatilho do banco apaga o campo de
    // propósito — cobrá-lo aqui trancava o pedido (Douglas 23/09/2026).
    const temChip = produtoTemChip(prodData?.nome);

    if (!isTV && temChip) {
      const linhasTelefoneTipoChip = (item.linhas_telefone || []) as any[];
      const quantidadeTipoChip = qtdTelefones;

      if (linhasTelefoneTipoChip.length > 0) {
        const todasLinhasTipoChip = linhasTelefoneTipoChip.slice(0, quantidadeTipoChip).every((linha: any) => {
          return linha && linha.tipo_chip && linha.tipo_chip !== null;
        });
        
        if (!todasLinhasTipoChip) {
          camposFaltantes.push('Tipo de Chip');
        }
      }
    }

    for (const catalogo of catalogos) {
      const codigo = catalogo.codigo;
      let preenchido = false;

      switch (codigo) {
        case 'quantidade':
          preenchido = item.quantidade > 0;
          break;
        case 'valor_unitario':
        case 'valor_unit':
          preenchido = item.valor_unit > 0;
          break;
        case 'valor_total':
          const subtotal = (item.quantidade || 0) * (item.valor_unit || 0);
          preenchido = subtotal > 0;
          break;
        case 'plano_nome':
        case 'produto':
          preenchido = !!item.plano_nome?.trim();
          break;
        case 'ddd_linha':
        case 'ddd':
          // ✅ Verificar se usa linhas_telefone (múltiplas linhas) ou campos antigos (linha única)
          const linhasTelefoneDDD = (item.linhas_telefone || []) as any[];
          const quantidadeDDD = qtdTelefones;
          
          if (linhasTelefoneDDD.length > 0) {
            // Produto com múltiplas linhas - validar array
            preenchido = linhasTelefoneDDD.slice(0, quantidadeDDD).every((linha: any) => {
              const dddValido = linha.ddd?.length === 2;
              const isNumeroNovo = linha.linha?.toUpperCase() === 'NÚMERO NOVO';
              const linhaValida = isNumeroNovo || (linha.linha && linha.linha.length >= 8);
              return dddValido && linhaValida;
            });
          } else {
            // Produto com linha única - usar campos antigos
            const isNumeroNovo = item.linha?.toUpperCase() === 'NÚMERO NOVO';
            const linhaValida = isNumeroNovo || (item.linha && item.linha.length >= 8);
            preenchido = item.ddd?.length === 2 && linhaValida;
          }
          break;
        case 'cessionario':
          preenchido = !!item.cessionario_id;
          break;
        case 'gestor_cliente':
        case 'gestor_id':
          // ✅ CORREÇÃO: Verificar se tem gestor E se o gestor tem RG/CPF preenchidos
          if (!item.gestor_id) {
            preenchido = false;
          } else {
            // Buscar dados do gestor para verificar RG/CPF
            const { data: gestorContato } = await supabase
              .from('cliente_contato')
              .select('rg, cpf')
              .eq('id', item.gestor_id)
              .single();
            
            if (!gestorContato || !gestorContato.rg || !gestorContato.cpf) {
              preenchido = false;
              // Adicionar campos específicos faltantes
              if (gestorContato && !gestorContato.rg) {
                camposFaltantes.push('RG do Gestor');
              }
              if (gestorContato && !gestorContato.cpf) {
                camposFaltantes.push('CPF do Gestor');
              }
            } else {
              preenchido = true;
            }
          }
          break;
        case 'endereco_instalacao':
          const end = (item.endereco_instalacao || {}) as any;
          preenchido = !!(end.cep && end.logradouro && end.numero && end.cidade && end.uf);
          break;
        case 'contrato_pf_tt':
          preenchido = !!String((item as { tt_contrato_pf?: string | null }).tt_contrato_pf ?? '').trim();
          break;
        case 'observacoes_item':
        case 'detalhes_pedido':
          preenchido = (item.observacoes_item?.length || 0) >= 10;
          break;
        case 'tipo_chip':
          if (!temChip) { preenchido = true; break; }
          const linhasTelefone = (item.linhas_telefone || []) as any[];
          const quantidade = qtdTelefones;
          preenchido = linhasTelefone.slice(0, quantidade).every((linha: any) => {
            return linha && linha.tipo_chip && linha.tipo_chip !== null;
          });
          break;
        case 'operadora_doadora':
          const operadora = item.operadora_doadora;
          const temTelefone = item.ddd?.length === 2 && item.linha?.length >= 8;
          
          if (temTelefone) {
            preenchido = !!operadora;
          } else {
            preenchido = true;
          }
          break;
        default:
          preenchido = true;
      }

      if (!preenchido) {
        camposFaltantes.push(catalogo.nome);
      }
    }

    return {
      valido: camposFaltantes.length === 0,
      camposFaltantes,
    };
  } catch (error) {
    console.error('Erro na validação do item:', error);
    return { valido: false, camposFaltantes: ['Erro ao validar'] };
  }
}

/**
 * Valida TODOS os itens de um pedido antes de fechar
 */
export async function validarPedidoParaFechar(pedidoId: string): Promise<ValidacaoPedido> {
  try {
    // Buscar itens sem joins
    const { data: itensData, error: itensError } = await supabase
      .from('itens_pedido')
      .select('*')
      .eq('pedido_id', pedidoId);

    if (itensError) throw itensError;
    const itens: any[] = itensData || [];
    const itensIncompletos: ItemIncompleto[] = [];

    for (const item of itens) {
      // Buscar produto separadamente
      const { data: prodData } = await supabase
        .from('produtos')
        .select('id, nome, grupo_obrigatoriedade_id')
        .eq('id', item.produto_id)
        .maybeSingle();

      // Régua universal produto × operadora + faixa da renovação — rodam mesmo
      // sem grupo configurado.
      const conflitosOperadora = [
        ...await conferirOperadoraItem(item, prodData?.nome),
        ...linhasSemFaixaRenovacao(item, prodData?.nome),
      ];

      if (!prodData?.grupo_obrigatoriedade_id) {
        if (conflitosOperadora.length > 0) {
          itensIncompletos.push({
            id: item.id,
            numero: `Item ${prodData?.nome || String(item.id).slice(0, 8)}`,
            plano_nome: item.plano_nome || 'Sem nome',
            camposFaltantes: conflitosOperadora,
          });
        }
        continue;
      }

      // Buscar configs sem join
      const { data: configsData } = await supabase
        .from('grupo_obrigatoriedades_config')
        .select('id, obrigatoriedade_id')
        .eq('grupo_id', prodData.grupo_obrigatoriedade_id)
        .eq('ativo', true)
        .eq('obrigatorio_em_pedido_fechado', true);

      if (!configsData || configsData.length === 0) {
        if (conflitosOperadora.length > 0) {
          itensIncompletos.push({
            id: item.id,
            numero: `Item ${prodData.nome || String(item.id).slice(0, 8)}`,
            plano_nome: item.plano_nome || 'Sem nome',
            camposFaltantes: conflitosOperadora,
          });
        }
        continue;
      }

      // Buscar catálogo separadamente
      const obrigIds = configsData.map((c: any) => c.obrigatoriedade_id);
      const { data: catalogData } = await supabase
        .from('obrigatoriedades_catalogo')
        .select('id, codigo, nome')
        .in('id', obrigIds);

    const catalogos: any[] = catalogData || [];
    const camposFaltantes: string[] = [...conflitosOperadora];

    // ✅ VALIDAÇÃO UNIVERSAL TIPO_CHIP (independente de obrigatoriedade configurada)
    // ⚠️ EXCEÇÃO: Produtos TV não precisam de tipo_chip
    const produtoNomeValidacao2 = item.plano_nome || '';
    const isTV2 = produtoNomeValidacao2.toUpperCase().includes('TV');
    const qtdTelefones = quantidadeLinhasTelefone(item.quantidade, item.linhas_telefone, (prodData?.nome || '').toUpperCase().includes('FIBRA'));

    // TT e renovação não têm chip: a linha já é do cliente e continua com o
    // mesmo número e o mesmo chip. O gatilho do banco apaga o campo de
    // propósito — cobrá-lo aqui trancava o pedido (Douglas 23/09/2026).
    const temChip2 = produtoTemChip(prodData?.nome);

    if (!isTV2 && temChip2) {
      const linhasTelefoneTipoChip = (item.linhas_telefone || []) as any[];
      const quantidadeTipoChip = qtdTelefones;

      if (linhasTelefoneTipoChip.length > 0) {
        const todasLinhasTipoChip = linhasTelefoneTipoChip.slice(0, quantidadeTipoChip).every((linha: any) => {
          return linha && linha.tipo_chip && linha.tipo_chip !== null;
        });
        
        if (!todasLinhasTipoChip) {
          camposFaltantes.push('Tipo de Chip');
        }
      }
    }

    for (const catalogo of catalogos) {
        const codigo = catalogo.codigo;
        let preenchido = false;

        switch (codigo) {
          case 'quantidade':
            preenchido = item.quantidade > 0;
            break;
          case 'valor_unitario':
          case 'valor_unit':
            preenchido = item.valor_unit > 0;
            break;
          case 'valor_total':
            // Calcular subtotal como quantidade × valor_unit
            const subtotal = (item.quantidade || 0) * (item.valor_unit || 0);
            preenchido = subtotal > 0;
            break;
          case 'plano_nome':
          case 'produto':
            preenchido = !!item.plano_nome?.trim();
            break;
          case 'ddd_linha':
          case 'ddd':
            // ✅ Verificar se usa linhas_telefone (múltiplas linhas) ou campos antigos (linha única)
            const linhasTelefoneDDD2 = (item.linhas_telefone || []) as any[];
            const quantidadeDDD2 = qtdTelefones;
            
            if (linhasTelefoneDDD2.length > 0) {
              // Produto com múltiplas linhas - validar array
              preenchido = linhasTelefoneDDD2.slice(0, quantidadeDDD2).every((linha: any) => {
                const dddValido = linha.ddd?.length === 2;
                const isNumeroNovo = linha.linha?.toUpperCase() === 'NÚMERO NOVO';
                const linhaValida = isNumeroNovo || (linha.linha && linha.linha.length >= 8);
                return dddValido && linhaValida;
              });
            } else {
              // Produto com linha única - usar campos antigos
              const isNumeroNovo = item.linha?.toUpperCase() === 'NÚMERO NOVO';
              const linhaValida = isNumeroNovo || (item.linha && item.linha.length >= 8);
              preenchido = item.ddd?.length === 2 && linhaValida;
            }
            break;
          case 'cessionario':
            preenchido = !!item.cessionario_id;
            break;
          case 'gestor_cliente':
          case 'gestor_id':
            // ✅ CORREÇÃO: Verificar se tem gestor E se o gestor tem RG/CPF preenchidos
            if (!item.gestor_id) {
              preenchido = false;
            } else {
              // Buscar dados do gestor para verificar RG/CPF
              const { data: gestorContato } = await supabase
                .from('cliente_contato')
                .select('rg, cpf')
                .eq('id', item.gestor_id)
                .single();
              
              if (!gestorContato || !gestorContato.rg || !gestorContato.cpf) {
                preenchido = false;
                // Adicionar campos específicos faltantes
                if (gestorContato && !gestorContato.rg) {
                  camposFaltantes.push('RG do Gestor');
                }
                if (gestorContato && !gestorContato.cpf) {
                  camposFaltantes.push('CPF do Gestor');
                }
              } else {
                preenchido = true;
              }
            }
            break;
          case 'endereco_instalacao':
            const end = (item.endereco_instalacao || {}) as any;
            preenchido = !!(end.cep && end.logradouro && end.numero && end.cidade && end.uf);
            break;
          case 'contrato_pf_tt':
            preenchido = !!String((item as { tt_contrato_pf?: string | null }).tt_contrato_pf ?? '').trim();
            break;
          case 'observacoes_item':
          case 'detalhes_pedido':
            preenchido = (item.observacoes_item?.length || 0) >= 10;
            break;
          case 'tipo_chip':
            if (!temChip2) { preenchido = true; break; }
            // ✅ Validar se todas as linhas (até a quantidade) têm tipo_chip
            const linhasTelefone = (item.linhas_telefone || []) as any[];
            const quantidade = qtdTelefones;
            preenchido = linhasTelefone.slice(0, quantidade).every((linha: any) => {
              return linha && linha.tipo_chip && linha.tipo_chip !== null;
            });
            break;
          case 'operadora_doadora':
            const operadora = item.operadora_doadora;
            const temTelefone = item.ddd?.length === 2 && item.linha?.length >= 8;
            
            if (temTelefone) {
              // Se tem telefone, operadora é obrigatória e deve ser válida
              preenchido = !!operadora;
            } else {
              // Se não tem telefone, operadora não é obrigatória
              preenchido = true;
            }
            break;
          default:
            preenchido = true;
        }

        if (!preenchido) {
          camposFaltantes.push(catalogo.nome);
        }
      }

      if (camposFaltantes.length > 0) {
        itensIncompletos.push({
          id: item.id,
          numero: `Item ${prodData.nome || item.id.slice(0, 8)}`,
          plano_nome: item.plano_nome || 'Sem nome',
          camposFaltantes,
        });
      }
    }

    return {
      valido: itensIncompletos.length === 0,
      itensIncompletos,
      totalItens: itens.length,
      itensCompletos: itens.length - itensIncompletos.length,
    };
  } catch (error) {
    console.error('Erro na validação:', error);
    throw error;
  }
}
